import {
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInMonths,
	differenceInSeconds,
	differenceInWeeks,
	differenceInYears
} from "date-fns"

import { getBlockNotePlainText, parseBlockNoteContent } from "./editor-content"

export { type ClassValue, cn } from "cnfast"

export function titleCase(str: string) {
	return str
		.replace(/_/g, " ")
		.toLowerCase()
		.replace(/\b\w/g, (char) => char.toUpperCase())
}

export function formatDate(date: Date) {
	const day = date.getDate()
	const dayWithSuffix =
		day >= 11 && day <= 13
			? `${day}th`
			: day % 10 === 1
				? `${day}st`
				: day % 10 === 2
					? `${day}nd`
					: day % 10 === 3
						? `${day}rd`
						: `${day}th`
	return date
		.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			hour12: false,
			hour: "2-digit",
			minute: "2-digit"
		})
		.replace(/(\d+)/, dayWithSuffix)
}

export function shortRelativeTime(date: Date) {
	const now = new Date()

	const years = differenceInYears(now, date)
	if (years) return `${years}y`

	const months = differenceInMonths(now, date)
	if (months) return `${months}mo`

	const weeks = differenceInWeeks(now, date)
	if (weeks) return `${weeks}w`

	const days = differenceInDays(now, date)
	if (days) return `${days}d`

	const hours = differenceInHours(now, date)
	if (hours) return `${hours}hr`

	const minutes = differenceInMinutes(now, date)
	if (minutes) return `${minutes}m`

	return `${Math.max(1, differenceInSeconds(now, date))}s`
}

export function enumFormat(text: string) {
	return titleCase(text.split(/(?=[A-Z])/).join(" "))
}

export const fancyIssueId = (id: string | number, slug: string) => {
	return `${slug}-${id}`.toUpperCase().replaceAll(/ /g, "-")
}

export const issueTitleWithId = (
	id: string | number,
	slug: string,
	title: string
) => `[${fancyIssueId(id, slug)}] ${title}`

export const sameSet = (a: string[], b: string[]) => {
	if (a.length !== b.length) return false
	const sortedA = [...a].sort()
	const sortedB = [...b].sort()
	return sortedA.every((value, index) => value === sortedB[index])
}

export const initials = (name: string) => {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
}

export {
	markdownToEditorContent,
	textToEditorContent,
	normalizeEditorContent
} from "./editor-conversion"
import { normalizeEditorContent } from "./editor-conversion"

export const getEditorPlainText = (value: string | null): string => {
	if (!value) return ""
	const content = parseBlockNoteContent(normalizeEditorContent(value))
	return content ? getBlockNotePlainText(content.blocks) : ""
}

export const getEditorMarkdown = (value: string | null): string => {
	if (!value) return ""
	return parseBlockNoteContent(normalizeEditorContent(value))?.markdown ?? ""
}

export const normalizeIssueDescription = (value: string | null | undefined) => {
	if (!value) return null
	const normalized = normalizeEditorContent(value)
	const content = parseBlockNoteContent(normalized)!
	return content.blocks.every(
		(block) =>
			block.type === "paragraph" &&
			Array.isArray(block.content) &&
			block.content.every((part) => part.type === "text" && !part.text) &&
			!block.children.length
	)
		? null
		: normalized
}

export const compareNames = (
	a: { id: string; name: string },
	b: { id: string; name: string }
) =>
	a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }) ||
	a.name.localeCompare(b.name, "en") ||
	a.id.localeCompare(b.id)
